import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'
import AiActionPreview from 'components/shared/tiptap/AiActionPreview'
import useStore from 'state/knovStore'
import api from 'api/api'
import * as cache from 'state/cache'
import useOpenRight from 'refactor/hooks/useOpenRight'
import openSourceAnswerThread from 'utils/openSourceAnswerThread'
import { getAnswerActionTokenPreview } from 'components/answers/aiActionPreviewProps'
import {
    actionRequestsWithMetadataPublicAt,
    aiActionInlineMetadataVisible,
    aiActionMetadataPublic,
    aiActionPopoverAvailable,
    answerOwnedByUser,
} from 'components/answers/aiActionMetadataVisibility'
import {
    actionTokenPromptMatchesAdjacentContent,
    actionTokenPromptUsesCard,
    textContentAfterElement,
    textContentBeforeElement,
} from 'components/answers/actionTokenPromptDisplay'
import { sameActionTokenElements } from './actionTokenElementList'
import styles from './answer-action-token-preview.module.scss'

const POPOVER_MARGIN = 8

const actionTokenElements = root =>
    Array.from(
        root?.querySelectorAll?.('.action-token[data-action-tag], .action-tag[data-action-tag]') ||
            [],
    )

const elementAiActionId = element =>
    element?.getAttribute?.('data-ai-action-id') || element?.getAttribute?.('data-action-id')

const tokenTrailingControl = element =>
    element.querySelector(
        '[aria-label="Show AI action details"], [data-action-token-remove], [data-action-tag-remove], .action-token-remove, .action-tag-remove',
    )

const tokenCommandElement = element =>
    element.querySelector(
        '.action-token-label, .action-tag-label, .action-token-command, .action-tag-command, .action-token-text',
    )

const tokenLabelTextNode = element =>
    Array.from(element?.childNodes || []).find(node => node.nodeType === window.Node.TEXT_NODE) ||
    null

const ensureTokenLabelTextNode = element => {
    const existingTextNode = tokenLabelTextNode(element)
    if (existingTextNode) return existingTextNode

    const textNode = document.createTextNode('')
    element.insertBefore(textNode, element.firstChild)
    return textNode
}

const tokenFallbackLabel = element => {
    const tag = element?.getAttribute?.('data-action-tag')?.trim()
    return tag ? `!${tag}` : 'AI action'
}

const tokenPromptElement = element => {
    return (
        element?.querySelector?.(
            '.action-token-prompt, .action-tag-prompt, .answer-action-token-prompt',
        ) || null
    )
}

const updateTokenTitle = element => {
    const label = element.getAttribute('data-action-display-name') || tokenFallbackLabel(element)
    const prompt = element.getAttribute('data-action-prompt-content')
    const promptPlacement = element.getAttribute('data-action-prompt-placement')
    element.title = prompt
        ? promptPlacement === 'before'
            ? `${prompt} ${label}`
            : `${label} ${prompt}`
        : label
}

const updateTokenDisplayName = (element, displayName) => {
    const label = typeof displayName === 'string' ? displayName.trim() : ''
    const commandElement = tokenCommandElement(element)
    const nextLabel = label || tokenFallbackLabel(element)

    if (commandElement) {
        commandElement.setAttribute('data-action-label', nextLabel)
        commandElement.setAttribute('aria-label', nextLabel)

        if (commandElement.getAttribute('data-action-label-render') === 'attr') {
            if (commandElement.textContent !== '') {
                commandElement.textContent = ''
            }
        } else if (commandElement.textContent !== nextLabel) {
            commandElement.textContent = nextLabel
        }
    } else if (!commandElement) {
        const labelTextNode = ensureTokenLabelTextNode(element)
        if (labelTextNode.nodeValue !== nextLabel) {
            labelTextNode.nodeValue = nextLabel
        }
    }

    if (label) {
        element.setAttribute('data-action-display-name', label)
    } else {
        element.removeAttribute('data-action-display-name')
    }

    updateTokenTitle(element)
}

// Returns whether the prompt text is visibly rendered inside the token (used to decide the
// block "generation card" treatment for long prompts).
const updateTokenPromptContent = (
    element,
    promptContent,
    priorContent = '',
    followingContent = '',
) => {
    const prompt = typeof promptContent === 'string' ? promptContent.trim() : ''
    const existingPrompt = tokenPromptElement(element)

    if (!prompt) {
        if (existingPrompt) {
            existingPrompt.hidden = false
            existingPrompt.removeAttribute('data-action-token-hidden-redundant-prompt')
            if (existingPrompt.matches('.action-token-prompt, .action-tag-prompt')) {
                existingPrompt.textContent = ''
                existingPrompt.removeAttribute('data-action-prompt-content')
                existingPrompt.setAttribute('data-action-token-empty-prompt', 'true')
            } else {
                existingPrompt.remove()
            }
        }
        element.removeAttribute('data-action-prompt-content')
        updateTokenTitle(element)
        return false
    }

    const promptMatchesAdjacentContent = actionTokenPromptMatchesAdjacentContent(
        prompt,
        priorContent,
        followingContent,
    )

    if (existingPrompt) {
        if (promptMatchesAdjacentContent) {
            existingPrompt.textContent = ''
            existingPrompt.hidden = true
            existingPrompt.setAttribute('data-action-token-hidden-redundant-prompt', 'true')
        } else {
            existingPrompt.hidden = false
            existingPrompt.removeAttribute('data-action-token-hidden-redundant-prompt')
            let promptTextNode =
                Array.from(existingPrompt.childNodes || []).find(
                    node => node.nodeType === window.Node.TEXT_NODE,
                ) || null
            if (!promptTextNode) {
                promptTextNode = document.createTextNode('')
                existingPrompt.appendChild(promptTextNode)
            }
            if (promptTextNode && promptTextNode.nodeValue !== prompt) {
                promptTextNode.nodeValue = prompt
            }
        }
        existingPrompt.setAttribute('data-action-prompt-content', prompt)
        existingPrompt.setAttribute('data-action-token-empty-prompt', 'false')
    }

    element.setAttribute('data-action-prompt-content', prompt)
    updateTokenTitle(element)
    return !!existingPrompt && !promptMatchesAdjacentContent
}

// Long prompts flip the token from the inline pill to the block generation card
// (ActionToken.module.scss keys off this attribute); the full prompt stays behind the
// existing click popover.
const updateTokenCardMode = (element, promptRendered) => {
    const prompt = element.getAttribute('data-action-prompt-content') || ''
    if (promptRendered && actionTokenPromptUsesCard(prompt)) {
        element.setAttribute('data-action-token-card', 'true')
    } else {
        element.removeAttribute('data-action-token-card')
    }
}

export default function AnswerActionTokenPreview({ containerRef, answer, quest }) {
    const aiModels = useStore(state => state.aiModels)
    const [tokenElements, setTokenElements] = useState([])
    const [activeToken, setActiveToken] = useState(null)
    const [position, setPosition] = useState(null)
    const isAnswerOwner = answerOwnedByUser(answer, window.gon?.currentUser?.id)
    const { openRight } = useOpenRight()

    useEffect(() => {
        const root = containerRef?.current
        if (!root) return undefined

        let disposed = false
        let clickCleanups = []
        let currentElements = []

        const refreshTokens = () => {
            if (disposed) return

            const nextElements = actionTokenElements(root)
            if (sameActionTokenElements(currentElements, nextElements)) return

            clickCleanups.forEach(cleanup => cleanup())
            clickCleanups = []
            currentElements = nextElements

            const infoElements = []
            nextElements.forEach((element, index) => {
                element.dataset.answerActionTokenIndex = String(index)
                const preview = getAnswerActionTokenPreview({
                    answer,
                    aiModels,
                    quest,
                    tag: element.getAttribute('data-action-tag'),
                    actionId: elementAiActionId(element),
                    actionIndex: index,
                })
                // Structured actions are private by default and WYSIWYG: everyone —
                // the owner included — sees the bare token (model name only) until
                // the owner shares the action publicly. The owner's tap still opens
                // the action card (share toggle + read-only input viewers).
                const metadataVisible = aiActionInlineMetadataVisible(preview.action)
                const popoverAvailable = aiActionPopoverAvailable({
                    action: preview.action,
                    isOwner: isAnswerOwner,
                })
                updateTokenDisplayName(element, preview.previewProps?.tokenDisplayName)
                const promptRendered = updateTokenPromptContent(
                    element,
                    metadataVisible ? preview.previewProps?.textInputValue : '',
                    textContentBeforeElement(root, element),
                    textContentAfterElement(root, element),
                )
                updateTokenCardMode(element, promptRendered)

                if (metadataVisible) {
                    infoElements.push(element)
                }

                if (!popoverAvailable) {
                    element.classList.remove('answer-readonly-action-token')
                    clickCleanups.push(() => {
                        element.removeAttribute('data-action-token-card')
                        delete element.dataset.answerActionTokenIndex
                    })
                    return
                }

                element.classList.add('answer-readonly-action-token')

                const onClick = event => {
                    const target = event.target
                    if (target?.closest?.('[data-action-token-remove], [data-action-tag-remove]'))
                        return

                    event.preventDefault()
                    event.stopPropagation()
                    setActiveToken({
                        element,
                        tag: element.getAttribute('data-action-tag'),
                        actionId: elementAiActionId(element),
                        actionIndex: index,
                    })
                }

                element.addEventListener('click', onClick)
                clickCleanups.push(() => {
                    element.removeEventListener('click', onClick)
                    element.classList.remove('answer-readonly-action-token')
                    element.removeAttribute('data-action-token-card')
                    delete element.dataset.answerActionTokenIndex
                })
            })
            setTokenElements(infoElements)
        }

        const observer = new MutationObserver(refreshTokens)
        observer.observe(root, { childList: true, subtree: true })
        refreshTokens()
        const refreshTimer = window.setTimeout(refreshTokens, 0)

        return () => {
            disposed = true
            window.clearTimeout(refreshTimer)
            observer.disconnect()
            clickCleanups.forEach(cleanup => cleanup())
            currentElements = []
        }
    }, [aiModels, answer, containerRef, isAnswerOwner, quest])

    useLayoutEffect(() => {
        if (!activeToken?.element) {
            setPosition(null)
            return undefined
        }

        const updatePosition = () => {
            const rect = activeToken.element.getBoundingClientRect()
            const width = Math.min(430, Math.max(300, window.innerWidth - POPOVER_MARGIN * 2))
            const left = Math.min(
                Math.max(POPOVER_MARGIN, rect.left),
                window.innerWidth - width - POPOVER_MARGIN,
            )
            setPosition({
                top: rect.bottom + POPOVER_MARGIN,
                left,
                width,
            })
        }

        updatePosition()
        window.addEventListener('resize', updatePosition)
        window.addEventListener('scroll', updatePosition, true)
        return () => {
            window.removeEventListener('resize', updatePosition)
            window.removeEventListener('scroll', updatePosition, true)
        }
    }, [activeToken])

    useEffect(() => {
        if (!activeToken) return undefined

        const closeOnEscape = event => {
            if (event.key === 'Escape') setActiveToken(null)
        }

        document.addEventListener('keydown', closeOnEscape, true)
        return () => {
            document.removeEventListener('keydown', closeOnEscape, true)
        }
    }, [activeToken])

    const preview = useMemo(() => {
        if (!activeToken)
            return { run: null, action: null, actionRequestIndex: -1, previewProps: null }
        return getAnswerActionTokenPreview({
            answer,
            aiModels,
            quest,
            tag: activeToken.tag,
            actionId: activeToken.actionId,
            actionIndex: activeToken.actionIndex,
        })
    }, [activeToken, aiModels, answer, quest])

    // Owner-only share toggle on the action card: persists metadata_public on the
    // backing action request so other viewers get the full generation card instead
    // of the bare token.
    const toggleActionMetadataSharing = () => {
        if (!answer?.id || !preview.action) return

        const actions = Array.isArray(answer.action_requests) ? answer.action_requests : []
        const actionIndex =
            preview.actionRequestIndex >= 0 &&
            actions[preview.actionRequestIndex] === preview.action
                ? preview.actionRequestIndex
                : actions.indexOf(preview.action)
        if (actionIndex < 0) return

        const nextActionRequests = actionRequestsWithMetadataPublicAt(
            actions,
            actionIndex,
            !aiActionMetadataPublic(preview.action),
        )
        const previousAnswer = answer

        cache.cacheAnswer({
            ...answer,
            action_requests: nextActionRequests,
        })
        api.updateAnswer(answer.id, {
            action_requests: nextActionRequests,
        }).catch(error => {
            cache.cacheAnswer(previousAnswer)
            console.error('Failed to update AI action sharing:', error)
            useStore.getState().showGlobalNotice('Failed to update sharing for this AI action.', {
                variant: 'error',
                durationMs: 5000,
            })
        })
    }

    const sharingToggleProps =
        isAnswerOwner && preview.action && answer?.id
            ? {
                  sharingPublic: aiActionMetadataPublic(preview.action),
                  onToggleSharing: toggleActionMetadataSharing,
              }
            : {}

    const infoButtons = tokenElements.map((element, index) =>
        createPortal(
            <button
                aria-label="Show AI action details"
                className={styles.infoButton}
                contentEditable={false}
                key={`action-token-info-${index}`}
                onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    setActiveToken({
                        element,
                        tag: element.getAttribute('data-action-tag'),
                        actionId: elementAiActionId(element),
                        actionIndex: Number(element.dataset.answerActionTokenIndex),
                    })
                }}
                type="button"
            >
                <Info size={12} strokeWidth={2.4} />
            </button>,
            element,
        ),
    )

    const stopModalEvent = event => {
        event.stopPropagation()
    }

    const absorbDismissLayerEvent = event => {
        event.preventDefault()
        event.stopPropagation()
    }

    const closeFromDismissLayer = event => {
        absorbDismissLayerEvent(event)
        setActiveToken(null)
    }

    const previewPortal =
        activeToken && position
            ? createPortal(
                  <div
                      aria-hidden="true"
                      className={styles.dismissLayer}
                      onClick={closeFromDismissLayer}
                      onMouseDown={absorbDismissLayerEvent}
                      onPointerDown={absorbDismissLayerEvent}
                      onTouchStart={absorbDismissLayerEvent}
                  />,
                  document.body,
              )
            : null

    const popover =
        activeToken && position
            ? createPortal(
                  <div
                      className={styles.popover}
                      onClick={stopModalEvent}
                      onMouseDown={stopModalEvent}
                      onPointerDown={stopModalEvent}
                      onTouchStart={stopModalEvent}
                      role="dialog"
                      style={{
                          top: position.top,
                          left: position.left,
                          width: position.width,
                      }}
                  >
                      {preview.previewProps ? (
                          <AiActionPreview
                              {...preview.previewProps}
                              {...sharingToggleProps}
                              readOnlyInputs
                              onOpenSourcePost={
                                  preview.previewProps.sourcePostReference
                                      ? () => {
                                            const { answerId } =
                                                preview.previewProps.sourcePostReference
                                            setActiveToken(null)
                                            openSourceAnswerThread({ answerId, openRight })
                                        }
                                      : null
                              }
                          />
                      ) : (
                          <div className={styles.emptyState}>
                              No AI action metadata was found for this token.
                          </div>
                      )}
                  </div>,
                  document.body,
              )
            : null

    return (
        <>
            {infoButtons}
            {previewPortal}
            {popover}
        </>
    )
}
